import { saveWindowSyncInfo } from 'modules/windowDesyncDetector';
import { useCallback, useEffect } from 'react';
import {
  useCurrentCaseId,
  useCurrentComparativeStudies,
  useSetCurrentCase,
} from './useCurrentCase';
import {
  addExtensionListener,
  focusEvent,
  removeExtensionListener,
  syncEvent,
  dispatchExtensionEvent,
} from 'domains/extension/extensionEventCreators';
import { RESPONSE_EVENTS as EXTENSION_RESPONSE_EVENTS } from 'domains/extension/constants';
import { useMatch } from 'react-router-dom';
import { AppRoutePath } from '../config/constants';
import { useOpenTabs } from './useOpenTabs';
import { PageTypes } from '../utils/pageTypes';
import { logger } from 'modules/logger';
import { WithApolloContext } from 'modules/Apollo/types';

/**
 * When mounted, registers a listener that will set the case context when a message is received from
 * another window. This hook will return a function `syncCase` that can send a message to other
 * windows to update their case context.
 */
export const useCaseSync = ({
  context,
}: WithApolloContext): ((id: string, studies?: ReadonlyArray<string>, focus?: boolean) => void) => {
  const [, setCurrentComparativeStudies] = useCurrentComparativeStudies({ context });
  const currentCaseId = useCurrentCaseId();
  const setCurrentCase = useSetCurrentCase();

  const { tabs } = useOpenTabs();
  const viewerTab = tabs.find((tab) => tab.type === PageTypes.VIEWER);
  const currentViewerCaseSmid = viewerTab?.worklistIds[0];

  const shouldPatientJacketStayInSync = currentViewerCaseSmid === currentCaseId;
  const isDockedJacket = useMatch(AppRoutePath.PATIENT_JACKET) != null;
  const isUndockedJacket = useMatch(AppRoutePath.PATIENT_JACKET_UNDOCKED) != null;
  const shouldSync = !(isDockedJacket || isUndockedJacket) || shouldPatientJacketStayInSync;

  useEffect(() => {
    if (!shouldSync) {
      return;
    }

    const handler = (
      event: CustomEvent<{
        from: Nullable<string>;
        to: string;
        studies: Nullable<string[]>;
        focus?: boolean;
      }>
    ) => {
      const { from, to, studies, focus = true } = event.detail;
      if (from != null && currentCaseId != null && from !== currentCaseId) {
        return;
      }

      if (studies != null) {
        setCurrentComparativeStudies({ caseSmid: to, studySmids: studies, fromOtherWindow: true });
      } else {
        setCurrentCase(to);
        // focus viewer/0, instead of viewer/1, viewer/2, etc.
        const paths = ['viewer/0', 'reporter'];

        if (focus && paths.some((path) => window.location.pathname.includes(path))) {
          logger.info(`[useCaseSync] focus ${window.location.pathname}`);
          dispatchExtensionEvent(focusEvent());
        }
      }
    };

    // @ts-expect-error [EN-10263] - TS2345 - Argument of type '(event: CustomEvent<{ from: string | null | undefined; to: string; studies: string[] | null | undefined; }>) => void' is not assignable to parameter of type 'EventListenerOrEventListenerObject'.
    addExtensionListener(EXTENSION_RESPONSE_EVENTS.PRIORS, handler);

    return () => {
      // @ts-expect-error [EN-10263] - TS2345 - Argument of type '(event: CustomEvent<{ from: string | null | undefined; to: string; studies: string[] | null | undefined; }>) => void' is not assignable to parameter of type 'EventListenerOrEventListenerObject'.
      removeExtensionListener(EXTENSION_RESPONSE_EVENTS.PRIORS, handler);
    };
  }, [currentCaseId, setCurrentCase, setCurrentComparativeStudies, shouldSync]);

  const syncCase = useCallback(
    (id: string, studies?: ReadonlyArray<string>, focus?: boolean) => {
      if (studies != null) {
        setCurrentComparativeStudies({ caseSmid: id, studySmids: studies });
      } else {
        setCurrentCase(id);
      }
      saveWindowSyncInfo(id);
      try {
        logger.info(`[useCaseSync] syncEvent ${id} with studies ${studies?.join(', ') ?? 'null'}`);
        dispatchExtensionEvent(syncEvent({ from: null, to: id, studies, focus }));
      } catch (err: any) {
        console.error(err);
      }
    },
    [setCurrentCase, setCurrentComparativeStudies]
  );

  return syncCase;
};
